import React from "react"
import styled from "styled-components"
import { rem } from "polished"
import { Link } from "gatsby"
import { media } from "@sr/common"
import BlockContent from "@sanity/block-content-to-react"
import CaseStudyEntryQuote from "./CaseStudyEntryQuote"
import {
  SemiBdTitle5,
  SemiBdTitle4,
  SemiBdTitle6,
  RegBody,
  Separator,
  SanityImg,
} from "@sr/gatsby-theme-ui"

const BlockRenderer = (props: any) => {
  const { node, children } = props
  const { style } = node

  switch (style) {
    case "h2":
      return (
        <SemiBdTitle4 as="h2" className="case-study-content__block-title-2">
          {children}
        </SemiBdTitle4>
      )
    case "h3":
      return (
        <SemiBdTitle5 as="h3" className="case-study-content__block-title-3">
          {children}
        </SemiBdTitle5>
      )
    case "normal":
      return (
        <RegBody className="case-study-content__block-normal">
          {children}
        </RegBody>
      )
  }

  return BlockContent.defaultSerializers.types.block(props)
}

const serializers = {
  marks: {
    externalLink: ({ mark, children }: any) => {
      const { blank, url } = mark
      return blank ? (
        <a href={url} rel="noopener noreferrer" target="_blank">
          {children}
        </a>
      ) : (
        <a href={url}>{children}</a>
      )
    },
    internalLink: ({ mark, children }: any) => {
      const path =
        mark &&
        mark.reference &&
        mark.reference.path &&
        mark.reference.path.current
          ? mark.reference.path.current
          : ""
      return <Link to={path}>{children}</Link>
    },
  },
  types: {
    block: BlockRenderer,
    caseStudyEntryBlockQuote: (props: any) => {
      const { citation, quote } = props.node
      return (
        <div className="case-study-content__quote-container">
          <SemiBdTitle6>{quote}</SemiBdTitle6>
          <RegBody>{citation}</RegBody>
        </div>
      )
    },
  },
}

export interface CaseStudyEntryContentPropsShape {
  quote: {
    quoteText: string
    quoteImg: SanityImg
    quoteCitation: string
  }
  contentData: any
}

const CaseStudyEntryContent = ({
  quote,
  contentData,
}: CaseStudyEntryContentPropsShape) => {
  return (
    <CaseStudyEntryContentStyled>
      <CaseStudyEntryQuote quote={quote} />
      <Separator
        showOnMobile={false}
        width={[50]}
        color="var(--color-gray300)"
        marginTop={[64, 40]}
        marginBottom={[72, 72]}
        thickness={0.5}
      />
      <article className="case-study-content__content-container">
        <BlockContent blocks={contentData} serializers={serializers} />
      </article>
    </CaseStudyEntryContentStyled>
  )
}

export default CaseStudyEntryContent

const CaseStudyEntryContentStyled = styled.section`
  margin: ${rem(64)} ${rem(32)} ${rem(0)};
  position: relative;

  @media ${media.midUp} {
    margin: ${rem(80)} ${rem(64)} ${rem(0)};
  }

  @media ${media.stMidUp} {
    padding: ${rem(16)} ${rem(32)};
    margin: ${rem(80)} ${rem(88)} ${rem(0)};

    max-width: 720px;
  }

  .case-study-content__quote-container {
    margin: ${rem(56)} ${rem(0)};
    padding: ${rem(24)} ${rem(16)};
    box-shadow: 0px 4px 50px rgba(0, 0, 0, 0.1);
    border-radius: 8px;

    @media ${media.midUp} {
      padding: ${rem(40)} ${rem(24)};
    }
    @media ${media.stMidUp} {
      padding: ${rem(40)} ${rem(32)};
    }
    h6 {
      color: var(--color-primary);
    }
    p {
      @media ${media.midUp} {
        margin-top: ${rem(4)};
      }
      @media ${media.stMidUp} {
        margin-top: ${rem(16)};
      }
    }
  }

  li {
    list-style-type: disc;
  }

  ul {
    margin-top: ${rem(32)};
    margin-left: ${rem(16)};
  }

  ol {
    margin-top: ${rem(32)};
    margin-left: ${rem(16)};
  }

  strong {
    font-weight: bold;
  }

  .case-study-content__quote-container + * {
    margin-top: 0;
  }

  .case-study-content__block-normal {
    margin-top: ${rem(32)};
  }

  .case-study-content__block-title-3 {
    margin-top: ${rem(40)};

    @media ${media.stMidUp} {
      margin-top: ${rem(56)};
    }
  }

  .case-study-content__block-title-2 {
    margin-top: ${rem(48)};

    @media ${media.stMidUp} {
      margin-top: ${rem(72)};
    }
  }
`
